import { PolyUtil, SphericalUtil } from "node-geometry-library";
import moment from "moment";
import * as turf from "@turf/turf";
import dayjs, { Dayjs } from "dayjs";

// ---------------------------------------------------------------------------
// Cutoff di prenotazione dei tour
// I clienti possono prenotare fino a BOOKING_CUTOFF_HOURS ore prima dell'inizio
// della fascia oraria scelta (prima era un limite di ~24h a livello di giorno).
// ---------------------------------------------------------------------------

// Ore minime di anticipo tra "adesso" e l'inizio della fascia oraria del tour.
export const BOOKING_CUTOFF_HOURS = 10;

// Ore di inizio delle fasce orarie selezionabili (9:00 ... 13:00).
export const TOUR_SLOT_START_HOURS = [9, 10, 11, 12, 13];

// Ora di riferimento per i tour SENZA selezione di fascia oraria: si assume che
// il tour inizi alle 9:00 (prima fascia della giornata).
export const DEFAULT_TOUR_START_HOUR = 9;

// Datetime di inizio di una fascia in un dato giorno.
const slotDateTime = (day: Dayjs, startHour: number): Dayjs =>
  dayjs(day).startOf("day").hour(startHour).minute(0).second(0).millisecond(0);

// true se quella fascia, in quel giorno, è ancora prenotabile: mancano almeno
// BOOKING_CUTOFF_HOURS ore all'inizio della fascia.
export const isSlotBookable = (
  day: Dayjs,
  startHour: number,
  now: Dayjs = dayjs()
): boolean =>
  slotDateTime(day, startHour).diff(now, "hour", true) >= BOOKING_CUTOFF_HOURS;

// Prima data selezionabile nel calendario: il primo giorno che ha ancora almeno
// una fascia prenotabile. Si usa l'ULTIMA fascia della giornata come riferimento
// così un giorno non viene escluso finché resta disponibile una fascia più tardi
// (il filtro fine sulle singole fasce avviene poi nel selettore della fascia).
export const getMinBookableDate = (now: Dayjs = dayjs()): Dayjs => {
  const latest = TOUR_SLOT_START_HOURS[TOUR_SLOT_START_HOURS.length - 1];
  let day = dayjs(now).startOf("day");
  // Cerchiamo al massimo pochi giorni avanti: se la fascia più tardi di oggi è
  // già troppo vicina si passa a domani, e così via.
  for (let i = 0; i < 4; i++) {
    if (isSlotBookable(day, latest, now)) return day;
    day = day.add(1, "day");
  }
  return day;
};

export const tourDistance = (pos:any, me:any) => {
  if(me && pos) {
    const lat1 = me[0]
    const lat2 = pos[0]
    const lon1 = me[1]
    const lon2 = pos[1]
    const R = 6371e3; // Earth's radius in meters
    const phi1 = lat1 * Math.PI / 180; // φ1 in radians
    const phi2 = lat2 * Math.PI / 180; // φ2 in radians
    const deltaPhi = (lat2 - lat1) * Math.PI / 180; // Δφ in radians
    const deltaLambda = (lon2 - lon1) * Math.PI / 180; // Δλ in radians

    const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
              Math.cos(phi1) * Math.cos(phi2) *
              Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = Math.round(R * c); // Distance in meters
    return distance;
  }
}

export const srcset = (image: string, size: number, rows = 1, cols = 1)  =>{
  return {
    src: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
    srcSet: `${image}?w=${size * cols}&h=${
      size * rows
    }&fit=crop&auto=format&dpr=2 2x`,
  };
}

export const randomChoice = (array: any[]): any => {
  const random = Math.floor(Math.random() * array.length);
  return array.splice(random, 1)[0];
};

export const toKmH = (speed: number | null): number => {
  if (!speed) {
    return 0;
  }
  speed = Math.round(speed * 3.6);
  return speed < 0 ? 0 : speed;
};

export const timeDiff = (startTime: number): string => {
  const milliseconds = moment().diff(new Date(startTime));
  const duration = moment.duration(milliseconds);
  const durationAsHours: number = Math.floor(duration.asHours());
  const restInMinutes: number = duration.minutes();
  const hours: string =
    durationAsHours > 1
      ? `${durationAsHours} h:`
      : durationAsHours > 0
      ? `${durationAsHours} h:`
      : "";
  const minutes = restInMinutes >= 1 ? restInMinutes : 0;
  // if less than 1 minute show just the seconds
  const minutesOrSeconds =
    milliseconds >= 60000
      ? `${minutes}:${moment.duration(milliseconds).seconds()} min`
      : `${moment.duration(milliseconds).seconds()} sec`;
  return `${hours}${minutesOrSeconds}`;
};

export const distanceFromStart = (
  lastPosition: any,
  start: any,
  returnNumber = false,
  distance = -1
): string | number => {
  if (distance === -1) {
    const d = SphericalUtil.computeDistanceBetween(
      { lat: lastPosition.latitude, lng: lastPosition.longitude },
      { lat: start.latitude, lng: start.longitude }
    );
    distance = d;
  }
  if (returnNumber) {
    return Math.round(distance);
  }
  return formatDistance(distance);
};

export const isOnTrack = (
  lastPosition: any,
  track: any[],
  tolerance = 25
): boolean => {
  return PolyUtil.isLocationOnPath(
    { lat: lastPosition.latitude, lng: lastPosition.longitude },
    track.map((t: any) => {
      return { lat: t.latitude, lng: t.longitude };
    }),
    tolerance
  );
};

export const calcDistance = (from: any, to: any, round = true) => {
  const d = turf.distance(
    turf.point([from.latitude, from.longitude]),
    turf.point([to.latitude, to.longitude]),
    { units: "meters" }
  );
  if (round) {
    return Math.round(d);
  }
  return d;
};

export const trackLength = (line: any) => {
  const trackToDo = turf.lineString(
    line.map((t: any) => [t.latitude, t.longitude])
  );
  return Math.round(turf.length(trackToDo, { units: "meters" }));
};

export const formatDistance = (meters: number, decimals = 0) => {
  if (meters > 1000) {
    if (decimals === 1) {
      return `${Math.round(meters / 100) / 10} km`;
    } else {
      return `${Math.round(meters / 1000)} Km`;
    }
  }
  return `${Math.round(meters)} m`;
};

export const calcTime = (spazio: any, toMin = false) => {
  const velocita = 10 / 3.6; // 10 km/h -> m/s
  const min = Math.round(spazio / velocita / 60); // s -> m
  const hours = Math.floor(min / 60);
  if (toMin) {
    return min;
  }

  let minutes = String(min % 60);
  if (min < 60) {
    return `${minutes} min`;
  }
  minutes = ("0" + minutes).slice(-2);
  return `${hours} h ${minutes} min`;
};

export const fitBoundCoords = (path: any) => {
  const box = turf.bbox(
    turf.lineString(
      path.map((c: any) => {
        return [c.latitude, c.longitude];
      })
    )
  );
  const minCoor = [box[1], box[0]];
  const maxCoor = [box[3], box[2]];
  const res = [minCoor, maxCoor];
  return res;
};


export const isInstalledApp = (success: any, error: any) => {
  if ('getInstalledRelatedApps' in window.navigator) {
    (window.navigator as any).getInstalledRelatedApps().then((relatedApps:any) => {
        if (relatedApps.length > 0) {
            console.log("Related app(s) are installed:");
            success(relatedApps)
        } else {
            console.log("No related apps found installed.");
            error("not installed app")
        }
    }).catch((e:any) => {
        console.error("Error checking for installed related apps:", error);
        error(e)
    });
  } else {
      console.warn("navigator.getInstalledRelatedApps() is not supported in this browser.");
      error("not supported browser")
  }
}

export let mobile = window.innerWidth <= 600;
window.onresize = () => {
  mobile = window.innerWidth <= 600;
}